import { Enemy } from './Enemy.js'
import { DARK_KING_SPRITE } from '../rendering/Sprites.js'
import { PLAYER_DAMAGE } from '../game/CombatBalance.js'

export class DarkKing extends Enemy {
  constructor(game, x, y, id) {
    super(game, x, y, id, 'darkKing')
    
    this.maxHealth = 900
    this.health = 900
    this.damage = PLAYER_DAMAGE.STANDARD
    this.moveSpeed = 4.3
    this.attackRange = 3
    this.attackCooldown = 1.4
    this.detectionRange = 50
    this.knockbackResistance = 0.9
    
    // Use bigger sprite
    this.sprite = DARK_KING_SPRITE
    
    // Boss-specific
    this.phase = 1
    this.isCharging = false
    this.chargeTimer = 0
    this.chargeCooldown = 0
    this.shockwaveCooldown = 0
    this.summonCooldown = 0
    this.fireballCooldown = 0
    
    this.chargeDirection = { x: 0, y: 0 }
    this.fireballs = []
    this.shockwaveRings = []
    this.pendingShockwaves = []
    this.summonedMinions = []
    this.encounterActive = false
    
    // LIGHT WEAKNESS - The Dark King takes damage in light!
    this.lightDamageTimer = 0
    this.lightDamageInterval = 1 // Take damage every second in light
    this.lightDamageAmount = 3 // Base damage per tick
  }

  setEncounterActive(active) {
    if (this.encounterActive === active) return
    this.encounterActive = active
    if (active) {
      this.game.showBossHealth?.('THE DARK KING')
      this.game.updateBossHealth?.(this.health / this.maxHealth)
    } else {
      this.resetAttackHazards()
      this.game.hideBossHealth?.()
    }
  }
  
  update(delta) {
    // Freeze the encounter outside the boss room so a checkpoint retry starts
    // with exactly the health the Dark King had when the player died.
    if (!this.encounterActive) {
      // A restored save can contain the final lethal hit before its short
      // knockback finished. Let that death complete even outside the room.
      if (this.pendingDeath) super.update(delta)
      return
    }

    super.update(delta)
    // A pending lethal hit can finish inside Enemy.update(). Do not continue
    // into the light-damage tick and award victory a second time.
    if (!this.isAlive) return
    
    // LIGHT DAMAGES THE DARK KING!
    this.updateLightDamage(delta)
    
    this.chargeCooldown -= delta
    this.shockwaveCooldown -= delta
    this.summonCooldown -= delta
    this.fireballCooldown -= delta
    
    // Handle charge movement
    if (this.isCharging) {
      const newX = this.x + this.chargeDirection.x * 10 * delta
      const newY = this.y + this.chargeDirection.y * 10 * delta
      
      if (!this.game.world.isSolid(newX, this.y)) this.x = newX
      if (!this.game.world.isSolid(this.x, newY)) this.y = newY

      this.chargeTimer -= delta
      if (this.chargeTimer <= 0) this.finishCharge()
    }

    this.updatePendingShockwaves(delta)
    
    // Update fireballs
    this.updateFireballs(delta)
    
    // Update shockwave rings
    this.updateShockwaves(delta)
    
    // Phase transitions
    if (this.health <= this.maxHealth * 0.25 && this.phase === 2) {
      this.phase = 3
      this.attackCooldown = 0.9
      this.damage = PLAYER_DAMAGE.HEAVY
      this.moveSpeed = 5.8
      this.lightDamageAmount = 1 // Even more resistant in phase 3
    } else if (this.health <= this.maxHealth * 0.55 && this.phase === 1) {
      this.phase = 2
      this.attackCooldown = 1.15
      this.damage = PLAYER_DAMAGE.HEAVY
      this.moveSpeed = 5
      this.lightDamageAmount = 2 // More resistant in phase 2
    }
    
    // Update boss health bar
    this.game.updateBossHealth?.(Math.max(0, this.health / this.maxHealth))
    
    // Clean up dead minions
    this.summonedMinions = this.summonedMinions.filter(m => m.isAlive)
  }
  
  updateLightDamage(delta) {
    // Check if it's daytime (light)
    if (!this.game.dayNight.isNight()) {
      this.lightDamageTimer += delta
      
      if (this.lightDamageTimer >= this.lightDamageInterval) {
        this.lightDamageTimer = 0
        
        // The Dark King withers in the light!
        this.health -= this.lightDamageAmount
        this.flashTimer = 0.15 // Visual feedback
        
        // Check for death
        if (this.health <= 0) {
          this.die()
        }
      }
    } else {
      // Regenerate slowly at night
      if (this.health < this.maxHealth) {
        this.health += delta * 2
      }
    }
  }
  
  updateAI(delta) {
    const player = this.game.player
    if (!player) return
    
    const dx = player.x - this.x
    const dy = player.y - this.y
    const dist = Math.sqrt(dx * dx + dy * dy)
    
    this.targetPosition = { x: player.x, y: player.y }
    
    // Boss attack patterns
    if (dist > 12 && this.chargeCooldown <= 0 && !this.isCharging) {
      this.chargeAttack()
    } else if (dist > 5 && dist < 15 && this.fireballCooldown <= 0) {
      this.fireballAttack()
    } else if (dist < 8 && dist > 3 && this.shockwaveCooldown <= 0) {
      this.shockwaveAttack()
    } else if (this.phase >= 2 && this.summonCooldown <= 0 && this.summonedMinions.length < (this.phase === 3 ? 5 : 3)) {
      this.summonMinions()
    }
  }
  
  chargeAttack() {
    this.chargeCooldown = this.phase === 3 ? 4 : (this.phase === 2 ? 5 : 6)
    this.isCharging = true
    
    const player = this.game.player
    const dx = player.x - this.x
    const dy = player.y - this.y
    const dist = Math.sqrt(dx * dx + dy * dy)
    
    this.chargeDirection = { x: dx / dist, y: dy / dist }
    this.chargeTimer = this.phase >= 2 ? 1 : 0.8
  }

  finishCharge() {
    if (!this.isAlive || !this.isCharging) return
    this.isCharging = false
    this.chargeTimer = 0

    const player = this.game.player
    const px = player.x - this.x
    const py = player.y - this.y
    if (Math.sqrt(px * px + py * py) < 3.5) {
      player.takeDamage(PLAYER_DAMAGE.HEAVY)
    }

    if (this.phase === 3) {
      this.shockwaveRings.push({
        x: this.x,
        y: this.y,
        radius: 0.5,
        maxRadius: 8,
        speed: 14,
        damage: PLAYER_DAMAGE.LIGHT,
        hasDamaged: false
      })
    }
  }
  
  fireballAttack() {
    this.fireballCooldown = this.phase === 3 ? 1.8 : (this.phase === 2 ? 2.4 : 3)
    
    const player = this.game.player
    const dx = player.x - this.x
    const dy = player.y - this.y
    
    // Fire more fireballs in a spread pattern
    const baseAngle = Math.atan2(dy, dx)
    const count = this.phase === 3 ? 7 : (this.phase === 2 ? 5 : 3)
    const spread = this.phase === 3 ? 1.2 : (this.phase === 2 ? 0.8 : 0.5)
    
    for (let i = 0; i < count; i++) {
      const angle = baseAngle + (i - (count - 1) / 2) * (spread / (count - 1))
      
      this.fireballs.push({
        x: this.x,
        y: this.y,
        dirX: Math.cos(angle),
        dirY: Math.sin(angle),
        speed: this.phase === 3 ? 13 : (this.phase === 2 ? 12 : 11),
        lifetime: 5,
        damage: this.phase === 3
          ? PLAYER_DAMAGE.STANDARD
          : PLAYER_DAMAGE.LIGHT
      })
    }
  }
  
  updateFireballs(delta) {
    const player = this.game.player
    
    for (let i = this.fireballs.length - 1; i >= 0; i--) {
      const fb = this.fireballs[i]
      
      fb.x += fb.dirX * fb.speed * delta
      fb.y += fb.dirY * fb.speed * delta
      fb.lifetime -= delta
      
      // Check hit player
      const dx = fb.x - player.x
      const dy = fb.y - player.y
      const dist = Math.sqrt(dx * dx + dy * dy)
      
      if (dist < 0.8) {
        player.takeDamage(fb.damage)
        this.fireballs.splice(i, 1)
        continue
      }
      
      // Remove if expired or hit wall
      if (fb.lifetime <= 0 || this.game.world.isSolid(fb.x, fb.y)) {
        this.fireballs.splice(i, 1)
      }
    }
  }
  
  shockwaveAttack() {
    this.shockwaveCooldown = this.phase === 3 ? 4 : (this.phase === 2 ? 5 : 6)
    
    // Create multiple expanding rings
    const ringCount = this.phase === 3 ? 3 : (this.phase === 2 ? 2 : 1)
    
    for (let i = 0; i < ringCount; i++) {
      this.pendingShockwaves.push({
        delay: i * 0.3,
        maxRadius: this.phase === 3 ? 12 : 10,
        speed: this.phase === 3 ? 14 : 12,
        damage: this.phase === 3
          ? PLAYER_DAMAGE.STANDARD
          : PLAYER_DAMAGE.LIGHT
      })
    }
  }

  updatePendingShockwaves(delta) {
    for (let i = this.pendingShockwaves.length - 1; i >= 0; i--) {
      const pending = this.pendingShockwaves[i]
      pending.delay -= delta
      if (pending.delay > 0) continue
      this.shockwaveRings.push({
        x: this.x,
        y: this.y,
        radius: 0.5,
        maxRadius: pending.maxRadius,
        speed: pending.speed,
        damage: pending.damage,
        hasDamaged: false
      })
      this.pendingShockwaves.splice(i, 1)
    }
  }

  resetAttackHazards() {
    this.isCharging = false
    this.chargeTimer = 0
    this.chargeDirection = { x: 0, y: 0 }
    this.fireballs = []
    this.shockwaveRings = []
    this.pendingShockwaves = []
  }
  
  updateShockwaves(delta) {
    const player = this.game.player
    
    for (let i = this.shockwaveRings.length - 1; i >= 0; i--) {
      const ring = this.shockwaveRings[i]
      
      ring.radius += ring.speed * delta
      
      // Check if player is at the ring's edge
      const dx = player.x - ring.x
      const dy = player.y - ring.y
      const playerDist = Math.sqrt(dx * dx + dy * dy)
      
      if (!ring.hasDamaged && Math.abs(playerDist - ring.radius) < 1) {
        player.takeDamage(ring.damage)
        ring.hasDamaged = true
      }
      
      // Remove when fully expanded
      if (ring.radius >= ring.maxRadius) {
        this.shockwaveRings.splice(i, 1)
      }
    }
  }
  
  summonMinions() {
    this.summonCooldown = this.phase === 3 ? 8 : 10
    
    const count = this.phase === 3 ? 3 : 2
    const minionTypes = ['shadowWolf', 'darkKnight']
    
    for (let i = 0; i < count; i++) {
      const angle = (i / count) * Math.PI * 2
      const spawnX = this.x + Math.cos(angle) * 5
      const spawnY = this.y + Math.sin(angle) * 5
      
      const type = minionTypes[Math.floor(Math.random() * minionTypes.length)]
      const minion = this.game.enemyManager.spawnEnemy(
        type,
        spawnX,
        spawnY,
        `king_minion_${Date.now()}_${i}`
      )
      
      if (minion) {
        this.summonedMinions.push(minion)
      }
    }
  }
  
  render(renderer) {
    // Draw shockwave rings
    for (const ring of this.shockwaveRings) {
      const alpha = 1 - (ring.radius / ring.maxRadius)
      renderer.ctx.strokeStyle = `rgba(153, 50, 204, ${alpha})`
      renderer.ctx.lineWidth = 4
      const screen = renderer.worldToScreen(ring.x, ring.y)
      const r = ring.radius * renderer.tileSize * renderer.zoom
      renderer.ctx.beginPath()
      renderer.ctx.arc(screen.x, screen.y, r, 0, Math.PI * 2)
      renderer.ctx.stroke()
    }
    
    // Draw fireballs
    for (const fb of this.fireballs) {
      renderer.fillCircle(fb.x, fb.y, 0.4, '#ff4500')
      renderer.fillCircle(fb.x, fb.y, 0.25, '#ffaa00')
    }
    
    // Draw charge trail
    if (this.isCharging) {
      renderer.fillCircle(this.x, this.y, 1, 'rgba(153, 50, 204, 0.4)')
    }
    
    // Draw the king (with light damage visual)
    if (this.flashTimer > 0) {
      // Flash yellow when taking light damage during day
      if (!this.game.dayNight.isNight()) {
        renderer.fillCircle(this.x, this.y, 1.2, 'rgba(255, 255, 100, 0.5)')
      }
    }
    
    super.render(renderer)
    
    // Draw light weakness indicator during day
    if (!this.game.dayNight.isNight()) {
      renderer.drawText('BURNING!', this.x, this.y - 1.5, '#ffff00', 10)
    }
  }
  
  die() {
    if (!this.isAlive) return false
    // Clean up
    this.resetAttackHazards()
    
    for (const minion of this.summonedMinions) {
      if (minion.isAlive) {
        minion.die()
      }
    }

    this.setEncounterActive(false)
    super.die()
    this.game.showVictoryScreen?.()
    return true
  }
  
  dropLoot() {
    this.game.itemManager.spawnItem({
      type: 'sword',
      id: 'legendary_sword',
      name: "King's Blade",
      damage: 50,
      count: 1
    }, { x: this.x, y: this.y })
    
    this.game.itemManager.spawnItem({
      type: 'armor',
      id: 'royal_armor',
      name: 'Royal Chestplate',
      defense: 20,
      count: 1
    }, { x: this.x, y: this.y })
  }
}
